import React from 'react';
import noop from 'lodash/noop';
import { xcss, Flex, Box } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useNudgeStep } from '@atlassian/jira-onboarding-quickstart-core/src/services';
import { BACKLOG_COMPLETE_SPRINT_NUDGE_ID } from '@atlassian/jira-software-onboarding-nudges--next/src/common/constants';
import { useRegisterNudgeTarget } from '@atlassian/jira-software-onboarding-nudges--next/src/common/ui/do-not-use-directly-portalled-nudge';
import { ConditionalNudgeWrapper } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/conditional-nudge-wrapper';
import { BacklogCompleteSprintNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-complete-sprint/async.tsx';
import { BacklogStartSprintNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/scrum-onboarding-tour/backlog-start-sprint/async.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import { useCardListContext } from '../../../../services/card-list-context';
import { useSprintLifecycleContext } from '../../../../services/sprint-lifecycle-context';
import CompleteSprintButton from '../../complete-sprint-button';
import Estimations from '../../estimations';
import SprintsMenu from '../../sprints-menu';
import StartSprintButton from '../../start-sprint-button-relay';
import messages from '../messages';

export const SprintHeader = () => {
	const { cardIds, cardListId, type, listName, isFirstList } = useCardListContext();
	const {
		canEditSprint,
		isStartSprintEnabled,
		isStartSprintButtonVisible,
		isCompleteSprintButtonVisible,
		isSprintMenuVisible,
	} = useSprintLifecycleContext();

	const { formatMessage } = useIntl();
	const [nudgeStep] = useNudgeStep();

	const { ref: startSprintButtonNudgeRef } = useRegisterNudgeTarget<HTMLButtonElement>(
		'start-sprint-button',
		isFirstList,
	);

	const renderStartSprintButton = () => {
		const totalCardAmount = cardIds.length;
		const isEmpty = totalCardAmount === 0;
		const shouldShowPrimaryButton = !isEmpty && isFirstList;
		const isStartSprintButtonDisabled = !isStartSprintEnabled || isEmpty;

		const appearanceValue = shouldShowPrimaryButton ? 'primary' : 'default';

		const button = (
			<StartSprintButton
				// This test-id is being used for the Jira Software NextGen Sprint Lifecycle pollinator test
				testId="software-backlog.card-list.card-list-header.sprint-header.start-sprint-button"
				sprintId={cardListId}
				appearance={isVisualRefreshEnabled() ? undefined : appearanceValue}
				isDisabled={
					fg('jsw-jet-yamato-fe-permissions-seq-sprint-actions')
						? isStartSprintButtonDisabled || !canEditSprint
						: isStartSprintButtonDisabled
				}
				shouldShowDisabledButtonHelper={!canEditSprint}
				ref={startSprintButtonNudgeRef}
				// Missing on original implementation
				onSave={noop}
				redirectTo=""
			>
				{formatMessage(messages.startSprint)}
			</StartSprintButton>
		);

		const possiblyNudgeWrappedButton = (
			<ConditionalNudgeWrapper
				conditionsToApplyWrapper={isFirstList}
				Wrapper={BacklogStartSprintNudgeAsync}
			>
				<Box>{button}</Box>
			</ConditionalNudgeWrapper>
		);

		if (
			fg('jsw-jet-yamato-fe-permissions-seq-sprint-actions')
				? isStartSprintButtonDisabled && canEditSprint
				: isStartSprintButtonDisabled
		) {
			return (
				<Tooltip
					content={
						totalCardAmount === 0
							? formatMessage(messages.disabledStartSprintButtonEmpty)
							: formatMessage(messages.disabledStartSprintButtonNoParallel)
					}
				>
					{possiblyNudgeWrappedButton}
				</Tooltip>
			);
		}
		return possiblyNudgeWrappedButton;
	};

	const renderCompleteSprintButton = () => (
		<ConditionalNudgeWrapper
			Wrapper={BacklogCompleteSprintNudgeAsync}
			conditionsToApplyWrapper={
				fg('jsw_perf_backlog_complete_sprint_button')
					? nudgeStep === BACKLOG_COMPLETE_SPRINT_NUDGE_ID
					: true
			}
		>
			<CompleteSprintButton
				sprintId={cardListId}
				// This test-id is being used for the Jira Software NextGen Sprint Lifecycle pollinator test
				testId="software-backlog.card-list.card-list-header.sprint-header.complete-sprint-button"
			>
				{formatMessage(messages.completeSprint)}
			</CompleteSprintButton>
		</ConditionalNudgeWrapper>
	);

	// This test-id is being used for the Jira Software NextGen Sprint Lifecycle pollinator test
	return (
		<Flex
			direction="row"
			alignItems="center"
			testId="software-backlog.card-list.card-list-header.sprint-header.estimations-and-actions-container"
		>
			<Estimations cardListId={cardListId} />
			<Flex alignItems="center" gap="space.100" xcss={cardMenuActionsStyles}>
				{isStartSprintButtonVisible && renderStartSprintButton()}
				{isCompleteSprintButtonVisible && renderCompleteSprintButton()}
				{isSprintMenuVisible && (
					<SprintsMenu cardListId={cardListId} cardListType={type} cardListName={listName} />
				)}
			</Flex>
		</Flex>
	);
};

const cardMenuActionsStyles = xcss({
	paddingLeft: 'space.100',

	':empty': {
		display: 'none',
	},
});
